import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";

import {
  GetOpdPrescriptionEntries,
  DeleteOpdPrescriptionEntry
} from "../../../../api/opd/opdPrescriptionEntryAPI";

const ListOpdPrescriptionEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * -----------------------------------
   * FETCH DATA
   * -----------------------------------
   */
  const fetchPrescriptionEntries = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetOpdPrescriptionEntries();
      if (response.success) {
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          date: item.date ? new Date(item.date).toLocaleDateString() : "",
          opdNo: item?.opdEntryId?.opdNo || "",
          patientName:
            (item?.opdEntryId?.patientId?.patientNamePrefix
              ? item?.opdEntryId?.patientId?.patientNamePrefix + " "
              : "") +
            (item?.opdEntryId?.patientId?.patientName || ""),
          sex: item?.opdEntryId?.patientId?.sex || "",
          age: item?.opdEntryId?.patientId?.age || "",
          mobileNo: item?.opdEntryId?.patientId?.mobileNo || "",
          address: item?.opdEntryId?.patientId?.address || "",

          doctor: item.consultantDoctorId?.name || "N/A",
          diagnosis: item.diagnosis || "N/A",
          complaints: (item.complaints || []).join("| \n"),
          history: item.historyOfPresentIllness || "N/A",
          examinations: (item.examinations || []).join("| \n"),
          allergies: item.anyKnownAllergies || "N/A",
          comorbid: item.comorbidConditions || "N/A",
          medication: item.medicationBeingTaken || "N/A",
          habits: item.habitsAdvice || "N/A",
          nextVisit: item.nextAppointmentDate
            ? new Date(item.nextAppointmentDate).toLocaleDateString()
            : ""
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Failed to fetch data");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchPrescriptionEntries();
  }, []);

  /**
   * -----------------------------------
   * SEARCH FILTER
   * -----------------------------------
   */
  useEffect(() => {
    const results = data.filter(item =>
      item.doctor.toLowerCase().includes(search.toLowerCase()) ||
      item.diagnosis.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  /**
   * -----------------------------------
   * ACTIONS
   * -----------------------------------
   */
  const handleEditClick = id => {
    navigate(`/opd/transaction/opd-prescription-entry/edit/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This prescription will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteOpdPrescriptionEntry(id);
          if (response.success) {
            showToast("success", response.message);
            setData(data.filter(item => item.id !== id));
            setFilteredData(filteredData.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message || "Delete failed");
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  /**
   * -----------------------------------
   * TABLE COLUMNS
   * -----------------------------------
   */
  const columns = [
    { name: "S.No", selector: row => row.index, width: "8%" },

    { name: "Date", selector: row => row.date, sortable: true, width: "8%" },
    { name: "Patient Name", selector: row => row.patientName, width: "8%" },
    { name: "Sex", selector: row => row.sex, width: "8%" },
    { name: "Age", selector: row => row.age, width: "8%" },
    { name: "Mobile No", selector: row => row.mobileNo, width: "8%" },
    { name: "Address", selector: row => row.address, width: "8%" },
    
    {
      name: "Doctor",
      selector: row => row.doctor,
      sortable: true,
      wrap: true, width: "10%"
    },

    {
      name: "Diagnosis",
      selector: row => row.diagnosis,
      sortable: true,
      wrap: true, width: "20%"
    },

    {
      name: "Complaints",
      selector: row => row.complaints,
      wrap: true, width: "20%"
    },

    {
      name: "History (HPI)",
      selector: row => row.history,
      wrap: true, width: "20%"
    },

    {
      name: "Examinations",
      selector: row => row.examinations,
      wrap: true, width: "20%"
    },

    {
      name: "Allergies",
      selector: row => row.allergies,
      wrap: true, width: "20%"
    },

    {
      name: "Comorbidities",
      selector: row => row.comorbid,
      wrap: true, width: "20%"
    },

    {
      name: "Medication",
      selector: row => row.medication,
      wrap: true, width: "20%"
    },

    {
      name: "Habits / Advice",
      selector: row => row.habits,
      wrap: true, width: "20%"
    },

    {
      name: "Next Visit",
      selector: row => row.nextVisit,
      sortable: true, width: "10%"
    },

    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            id={`edit-${row.id}`}
            onClick={() => handleEditClick(row.id)}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            id={`delete-${row.id}`}
            onClick={() => handleDeleteClick(row.id)}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "15%"
    }
  ];


  /**
   * -----------------------------------
   * CSV EXPORT
   * -----------------------------------
   */
  const headers = [
    { label: "S.No", key: "index" },
    { label: "Date", key: "date" },
    { label: "Doctor", key: "doctor" },
    { label: "Diagnosis", key: "diagnosis" },
    { label: "Complaints", key: "complaints" },
    { label: "History (HPI)", key: "history" },
    { label: "Examinations", key: "examinations" },
    { label: "Allergies", key: "allergies" },
    { label: "Comorbidities", key: "comorbid" },
    { label: "Medication", key: "medication" },
    { label: "Habits / Advice", key: "habits" },
    { label: "Next Visit", key: "nextVisit" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="opd-prescriptions.csv">
      <button className="btn btn-primary" id="export">
        Export
      </button>
      <UncontrolledTooltip target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>
  );

  return (
    <Fragment>
      <Breadcrumb parent="OPD" title="Prescription List" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>OPD Prescription Entries</h5>
              <Link to="/opd/transaction/opd-prescription-entry/add">
                <button className="btn btn-primary" id="create">
                  Add Prescription
                </button>
                <UncontrolledTooltip target="create">
                  Add Prescription
                </UncontrolledTooltip>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search by Doctor or Diagnosis"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListOpdPrescriptionEntry;
